import { Flex, Text, Tooltip } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import DocumentAvailabilityIcon from '@/components/shared/DocumentAvailabilityIcon';
import { useTreeContext } from '@/components/shared/Tree';
import TreeNode from '@/components/ui/TreeNode';
import { IconFolder } from '@/components/ui/icons/IconFolder';
import { TreeNodeFolder, isOrganizationRootPersonalFolder } from '@/entities/Tree';
import { TreeStore } from '@/stores/local/shared/TreeStore';

import s from './TreeFolder.module.scss';

const PERSONAL_FOLDER_TOOLTIP = 'Документы в этой папке доступны только вам';

type Props = {
  store: TreeStore;
  model: TreeNodeFolder;
  actions?: React.ReactNode;
  onClick: () => void;
  className?: string;
};

const TreeFolder: React.FC<Props> = ({ store, model, actions, onClick, className }) => {
  const { renderNode } = useTreeContext();
  const { isOpen, children, level, meta } = store;
  const isAvailabilityLocked = !model.contentAvailable;

  const folderGlyph = <IconFolder size={16} className={cn(isAvailabilityLocked && s['root__folder-muted'])} />;

  const availabilityHint = isAvailabilityLocked ? (
    <DocumentAvailabilityIcon
      reason={model.availabilityBlockReason}
      variant="folder"
      className={s['root__status-icon']}
    />
  ) : null;

  const personalHint =
    !isAvailabilityLocked && isOrganizationRootPersonalFolder(store, model) ? (
      <Tooltip label={PERSONAL_FOLDER_TOOLTIP} position="right" multiline maw={280} openDelay={200}>
        <span className={s['root__personal-icon']} role="img" aria-label="Личная папка: документы доступны только вам">
          <IconUser size={17} stroke={2.25} />
        </span>
      </Tooltip>
    ) : null;

  const icon = (
    <Flex align="center" gap={6} className={s['root__icons']} wrap="nowrap">
      {folderGlyph}
      {availabilityHint}
      {personalHint}
    </Flex>
  );

  const title = isAvailabilityLocked ? (
    <Text component="span" size="sm" lineClamp={1} className={s['root__title-muted']} style={{ flex: 1, minWidth: 0 }}>
      {model.name}
    </Text>
  ) : (
    model.name
  );

  return (
    <TreeNode
      className={className}
      isOpen={isOpen}
      withArrow={model.hasChildren}
      isLoading={meta.isLoading}
      onClick={onClick}
      level={level}
      icon={icon}
      actions={actions}
      isContentLocked={isAvailabilityLocked}
      title={title}
    >
      {isOpen && (
        <>
          {children.map(renderNode)}
          {children.length === 0 && <TreeNode.Empty level={level} />}
        </>
      )}
    </TreeNode>
  );
};

export default observer(TreeFolder);
